import type { HsCamera } from '../../engine-new/objects/cameras/camera.class';
import type { HsGraphicLayer } from '../objects/graphic-layer.class';
import { HS_DEFAULT_CAD_EFFECTS_SETTINGS, type HsCadEffectsSettings } from '../types/cad-effects-settings.interface';
import type { HsLayerPostProcess } from '../types/layer-post-process.interface';
import {
  HS_CAD_AO_FRAGMENT_SOURCE,
  HS_CAD_COMPOSITE_FRAGMENT_SOURCE,
  HS_CAD_FULLSCREEN_VERTEX_SOURCE,
} from './cad-post-process.shaders';

interface PassProgram {
  readonly program: WebGLProgram;
  readonly uniforms: Map<string, WebGLUniformLocation | null>;
}

const AO_SAMPLES_NAVIGATION: number = 6;
const AO_SAMPLES_IDLE: number = 16;
const AO_SAMPLES_MAX: number = 32;
const EDGE_PIXEL_ANGLE_MIN: number = 1e-5;
// Bilateral occlusion filter half-width in pixels: 4x4 taps at rest, 2x2 while the camera moves.
const AO_BLUR_RADIUS_IDLE: number = 2;
const AO_BLUR_RADIUS_NAVIGATION: number = 1;
/** Duration of the fade back to the full-quality look after the camera stops, so it does not pop. */
export const HS_CAD_RESTORE_FADE_MS: number = 180;

/**
 * Screen-space CAD effects (ambient occlusion, edges, tonemapping, gradient background) applied to a layer after its
 * MSAA resolve. Each stage reads only the resolved color and depth textures, so the passes stay independent of the
 * scene shaders and map one-to-one onto a future WebGPU backend.
 */
export class HsCadPostProcess implements HsLayerPostProcess {
  public outputTexture: WebGLTexture | null = null;
  public readonly requiresDepth: boolean = true;
  public readonly maskPointClouds: boolean = true;
  public clearColor: readonly [number, number, number] = [1, 1, 1];

  private settings: HsCadEffectsSettings;
  private navigating: boolean = false;
  private restoreStartedAt: number = Number.NEGATIVE_INFINITY;
  private stale: boolean = true;
  private width: number = 0;
  private height: number = 0;
  private aoWidth: number = 0;
  private aoHeight: number = 0;
  private aoTexture: WebGLTexture | null = null;
  private aoFramebuffer: WebGLFramebuffer | null = null;
  private outputFramebuffer: WebGLFramebuffer | null = null;
  private vertexArray: WebGLVertexArrayObject | null = null;
  private aoPass: PassProgram | null = null;
  private compositePass: PassProgram | null = null;
  private failed: boolean = false;
  private failureReason: string = '';

  constructor(
    settings: HsCadEffectsSettings = HS_DEFAULT_CAD_EFFECTS_SETTINGS,
    private readonly now: () => number = () => performance.now()
  ) {
    this.settings = { ...settings };
  }

  public get isFailed(): boolean {
    return this.failed;
  }

  /** Shader compile or link log when the passes are unavailable; empty otherwise. */
  public get failureMessage(): string {
    return this.failureReason;
  }

  public setSettings(settings: HsCadEffectsSettings): void {
    this.settings = { ...settings };
    this.stale = true;
  }

  /**
   * Switches between the cheap navigation quality and the full idle quality. Navigation drops the key light and
   * sharpening and uses a smaller occlusion filter; after the camera stops they fade back in over
   * {@link HS_CAD_RESTORE_FADE_MS}.
   */
  public setNavigating(navigating: boolean): void {
    if (this.navigating !== navigating) {
      this.navigating = navigating;
      this.stale = true;
      if (!navigating) {
        this.restoreStartedAt = this.now();
      }
    }
  }

  /** Share of the idle-only effects currently applied: 0 while navigating, rising to 1 during the fade. */
  public restoreFactor(): number {
    if (this.navigating) {
      return 0;
    }
    return Math.min(1, Math.max(0, (this.now() - this.restoreStartedAt) / HS_CAD_RESTORE_FADE_MS));
  }

  public isStale(): boolean {
    return this.stale || this.restoreFactor() < 1;
  }

  public run(gl: WebGL2RenderingContext, camera: HsCamera, layer: HsGraphicLayer): boolean {
    if (this.failed || layer.depthTexture === null || layer.texture === null) {
      return false;
    }
    if (!this.ensureResources(gl, layer)) {
      return false;
    }
    const projection: readonly number[] = camera.projectionMatrix.values;
    const orthographic: boolean = projection[15] === 1;
    const aoScale: number = this.navigating ? 1 / this.settings.navigationScale : 1;
    const aoEnabled: boolean = this.settings.ssaoEnabled && this.settings.ssaoIntensity > 0;
    const restore: number = this.restoreFactor();
    this.resizeAo(gl, aoScale);

    gl.disable(gl.BLEND);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);
    gl.depthMask(false);
    gl.bindVertexArray(this.vertexArray);

    if (aoEnabled && this.aoPass !== null) {
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.aoFramebuffer);
      gl.viewport(0, 0, this.aoWidth, this.aoHeight);
      gl.useProgram(this.aoPass.program);
      this.setDepthUniforms(gl, this.aoPass, projection, orthographic);
      gl.uniform1f(this.uniform(gl, this.aoPass, 'u_scale'), this.width / this.aoWidth);
      gl.uniform1f(this.uniform(gl, this.aoPass, 'u_radiusPx'), Math.max(2, this.settings.ssaoRadius * this.height));
      gl.uniform1i(
        this.uniform(gl, this.aoPass, 'u_samples'),
        Math.min(AO_SAMPLES_MAX, this.navigating ? AO_SAMPLES_NAVIGATION : AO_SAMPLES_IDLE)
      );
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, layer.depthTexture);
      gl.uniform1i(this.uniform(gl, this.aoPass, 'u_depth'), 1);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    if (this.compositePass !== null) {
      const pass: PassProgram = this.compositePass;
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.outputFramebuffer);
      gl.viewport(0, 0, this.width, this.height);
      gl.useProgram(pass.program);
      this.setDepthUniforms(gl, pass, projection, orthographic);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, layer.texture);
      gl.uniform1i(this.uniform(gl, pass, 'u_color'), 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, layer.depthTexture);
      gl.uniform1i(this.uniform(gl, pass, 'u_depth'), 1);
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, this.aoTexture);
      gl.uniform1i(this.uniform(gl, pass, 'u_ao'), 2);
      gl.uniform1f(this.uniform(gl, pass, 'u_aoScale'), this.width / this.aoWidth);
      gl.uniform1f(this.uniform(gl, pass, 'u_aoIntensity'), aoEnabled ? this.settings.ssaoIntensity : 0);
      gl.uniform1i(
        this.uniform(gl, pass, 'u_aoBlurRadius'),
        this.navigating ? AO_BLUR_RADIUS_NAVIGATION : AO_BLUR_RADIUS_IDLE
      );
      gl.uniform1f(
        this.uniform(gl, pass, 'u_edgeIntensity'),
        this.settings.edgesEnabled ? this.settings.edgeIntensity : 0
      );
      // Angular size of one pixel; scales the crease threshold with resolution and field of view.
      const pixelAngle: number = Math.max(EDGE_PIXEL_ANGLE_MIN, 2 / (this.height * projection[5]));
      gl.uniform1f(this.uniform(gl, pass, 'u_pixelAngle'), pixelAngle);
      gl.uniform1i(this.uniform(gl, pass, 'u_edgeWidth'), this.settings.edgeWidth >= 2 ? 2 : 1);
      gl.uniform1f(this.uniform(gl, pass, 'u_tonemap'), this.settings.tonemapEnabled ? 1 : 0);
      gl.uniform1f(this.uniform(gl, pass, 'u_tonemapStrength'), this.settings.tonemapStrength);
      gl.uniform1f(this.uniform(gl, pass, 'u_saturation'), this.settings.saturation);
      gl.uniform1f(this.uniform(gl, pass, 'u_warmth'), this.settings.warmth);
      // The key light reconstructs normals from depth and sharpening amplifies shimmer, so both apply to the
      // settled frame only and fade in after the camera stops.
      gl.uniform1f(this.uniform(gl, pass, 'u_keyLight'), this.settings.keyLight * restore);
      gl.uniform1f(this.uniform(gl, pass, 'u_sharpen'), this.settings.sharpen * restore);
      gl.uniform1f(this.uniform(gl, pass, 'u_gradient'), this.settings.gradientBackground ? 1 : 0);
      gl.uniform1f(this.uniform(gl, pass, 'u_vignette'), this.settings.vignette ? 1 : 0);
      gl.uniform3f(this.uniform(gl, pass, 'u_clearColor'), ...this.clearColor);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    gl.bindVertexArray(null);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.activeTexture(gl.TEXTURE0);
    gl.depthMask(true);
    gl.enable(gl.BLEND);
    gl.enable(gl.DEPTH_TEST);
    this.stale = false;
    return true;
  }

  public dispose(gl: WebGL2RenderingContext): void {
    gl.deleteTexture(this.aoTexture);
    gl.deleteTexture(this.outputTexture);
    gl.deleteFramebuffer(this.aoFramebuffer);
    gl.deleteFramebuffer(this.outputFramebuffer);
    gl.deleteVertexArray(this.vertexArray);
    [this.aoPass, this.compositePass].forEach((pass: PassProgram | null) => {
      if (pass !== null) {
        gl.deleteProgram(pass.program);
      }
    });
    this.aoTexture = null;
    this.outputTexture = null;
    this.aoFramebuffer = null;
    this.outputFramebuffer = null;
    this.vertexArray = null;
    this.aoPass = null;
    this.compositePass = null;
    this.width = 0;
    this.height = 0;
  }

  /** Approximate GPU memory of the intermediate targets in bytes (output RGBA8 plus ambient occlusion R8). */
  public estimateMemoryBytes(): number {
    return this.width * this.height * 4 + this.aoWidth * this.aoHeight;
  }

  private setDepthUniforms(
    gl: WebGL2RenderingContext,
    pass: PassProgram,
    projection: readonly number[],
    orthographic: boolean
  ): void {
    gl.uniform4f(this.uniform(gl, pass, 'u_proj'), projection[0], projection[5], projection[10], projection[14]);
    gl.uniform1f(this.uniform(gl, pass, 'u_ortho'), orthographic ? 1 : 0);
    gl.uniform2f(this.uniform(gl, pass, 'u_size'), this.width, this.height);
  }

  private uniform(gl: WebGL2RenderingContext, pass: PassProgram, name: string): WebGLUniformLocation | null {
    if (!pass.uniforms.has(name)) {
      pass.uniforms.set(name, gl.getUniformLocation(pass.program, name));
    }
    return pass.uniforms.get(name) ?? null;
  }

  private ensureResources(gl: WebGL2RenderingContext, layer: HsGraphicLayer): boolean {
    if (this.aoPass === null) {
      this.aoPass = this.buildProgram(gl, HS_CAD_AO_FRAGMENT_SOURCE);
      this.compositePass = this.buildProgram(gl, HS_CAD_COMPOSITE_FRAGMENT_SOURCE);
      this.vertexArray = gl.createVertexArray();
      this.aoFramebuffer = gl.createFramebuffer();
      this.outputFramebuffer = gl.createFramebuffer();
      if (
        this.aoPass === null ||
        this.compositePass === null ||
        this.vertexArray === null ||
        this.aoFramebuffer === null ||
        this.outputFramebuffer === null
      ) {
        this.failed = true;
        return false;
      }
    }
    if (this.width !== layer.getWidth() || this.height !== layer.getHeight() || this.outputTexture === null) {
      this.width = layer.getWidth();
      this.height = layer.getHeight();
      gl.deleteTexture(this.outputTexture);
      this.outputTexture = this.createTexture(gl, gl.RGBA8, gl.RGBA, this.width, this.height);
      gl.bindFramebuffer(gl.FRAMEBUFFER, this.outputFramebuffer);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.outputTexture, 0);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      this.aoWidth = 0;
      this.stale = true;
    }
    return this.outputTexture !== null;
  }

  private resizeAo(gl: WebGL2RenderingContext, scale: number): void {
    const width: number = Math.max(1, Math.round(this.width / scale));
    const height: number = Math.max(1, Math.round(this.height / scale));
    if (width === this.aoWidth && height === this.aoHeight) {
      return;
    }
    this.aoWidth = width;
    this.aoHeight = height;
    gl.deleteTexture(this.aoTexture);
    this.aoTexture = this.createTexture(gl, gl.R8, gl.RED, width, height);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.aoFramebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.aoTexture, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  private createTexture(
    gl: WebGL2RenderingContext,
    internalFormat: number,
    format: number,
    width: number,
    height: number
  ): WebGLTexture | null {
    const texture: WebGLTexture | null = gl.createTexture();
    if (texture === null) {
      return null;
    }
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, width, height, 0, format, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, null);
    return texture;
  }

  private buildProgram(gl: WebGL2RenderingContext, fragmentSource: string): PassProgram | null {
    const vertex: WebGLShader | null = this.compile(gl, gl.VERTEX_SHADER, HS_CAD_FULLSCREEN_VERTEX_SOURCE);
    const fragment: WebGLShader | null = this.compile(gl, gl.FRAGMENT_SHADER, fragmentSource);
    const program: WebGLProgram | null = gl.createProgram();
    if (vertex === null || fragment === null || program === null) {
      return null;
    }
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);
    if (gl.getProgramParameter(program, gl.LINK_STATUS) !== true) {
      this.failureReason = `link failed: ${gl.getProgramInfoLog(program) ?? ''}`;
      gl.deleteProgram(program);
      return null;
    }
    return { program, uniforms: new Map() };
  }

  private compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
    const shader: WebGLShader | null = gl.createShader(type);
    if (shader === null) {
      return null;
    }
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (gl.getShaderParameter(shader, gl.COMPILE_STATUS) !== true) {
      this.failureReason = `compile failed: ${gl.getShaderInfoLog(shader) ?? ''}`;
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }
}
